import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log('Supabase Config Check:', {
  url: supabaseUrl,
  key: supabaseAnonKey ? `${supabaseAnonKey.substring(0, 10)}...` : 'undefined',
  hasUrl: !!supabaseUrl,
  hasKey: !!supabaseAnonKey
});

// Check if we have valid Supabase credentials
const hasValidCredentials = supabaseUrl && 
  supabaseAnonKey && 
  supabaseUrl !== 'your_supabase_url_here' && 
  supabaseAnonKey !== 'your_supabase_anon_key_here' &&
  !supabaseUrl.includes('your_supabase_url_here') &&
  !supabaseAnonKey.includes('your_supabase_anon_key_here') &&
  supabaseUrl.startsWith('https://') &&
  supabaseUrl.includes('.supabase.co');

console.log('Supabase credentials valid:', hasValidCredentials);

// Use a dummy but valid URL structure if credentials are not configured
const fallbackUrl = 'https://placeholder.supabase.co';
const fallbackKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBsYWNlaG9sZGVyIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NDUxOTI4MDAsImV4cCI6MTk2MDc2ODgwMH0.placeholder';

export const supabase = createClient(
  hasValidCredentials ? supabaseUrl : fallbackUrl,
  hasValidCredentials ? supabaseAnonKey : fallbackKey
);

// Export a flag to check if Supabase is properly configured
export const isSupabaseConfigured = hasValidCredentials;

// Test database connection
export const testConnection = async () => {
  if (!isSupabaseConfigured) {
    console.log('Supabase not configured - using mock data');
    return false;
  }

  try {
    console.log('Testing database connection...');
    
    // Test with a simple query that should work on any Supabase instance
    const { data, error } = await supabase
      .from('impact_stats')
      .select('count', { count: 'exact', head: true });
    
    if (error) {
      console.error('Database connection test failed:', error);
      
      // If table doesn't exist, that's actually OK - it means we're connected but need to set up data
      if (error.code === 'PGRST106' || error.message.includes('relation') || error.message.includes('does not exist')) {
        console.log('Database connected but table may not exist yet');
        return true;
      }
      
      return false;
    }
    
    console.log('Database connection successful');
    return true;
  } catch (error) {
    console.error('Database connection error:', error);
    return false;
  }
};

// Create storage bucket for issue reports if it doesn't exist
export const ensureIssueBucketExists = async () => {
  if (!isSupabaseConfigured) {
    console.log('Supabase not configured - skipping bucket creation');
    return false;
  }

  try {
    console.log('Checking if issue-reports bucket exists...');
    
    // Check if the bucket exists
    const { data: buckets, error } = await supabase.storage.listBuckets();
    
    if (error) {
      console.error('Error checking buckets:', error);
      return false;
    }
    
    const bucketExists = buckets.some(bucket => bucket.name === 'issue-reports');
    
    if (!bucketExists) {
      console.log('Creating issue-reports bucket...');
      const { error: createError } = await supabase.storage.createBucket('issue-reports', {
        public: false,
        fileSizeLimit: 10485760, // 10MB
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/webm', 'video/quicktime']
      });
      
      if (createError) {
        console.error('Error creating bucket:', createError);
        return false;
      }
      
      console.log('Bucket created successfully');
      return true;
    }
    
    console.log('Bucket already exists');
    return true;
  } catch (error) {
    console.error('Error ensuring bucket exists:', error);
    return false;
  }
};

// Database types
export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          username: string;
          avatar_url: string;
          level: string;
          eco_coins: number;
          streak: number;
          location: string;
          co2_saved: number;
          waste_reduced: number;
          trees_planted: number;
          water_saved: number;
          energy_saved: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id: string;
          username: string;
          avatar_url?: string;
          level?: string;
          eco_coins?: number;
          streak?: number;
          location?: string;
          co2_saved?: number;
          waste_reduced?: number;
          trees_planted?: number;
          water_saved?: number;
          energy_saved?: number;
        };
        Update: {
          username?: string;
          avatar_url?: string;
          level?: string;
          eco_coins?: number;
          streak?: number;
          location?: string;
          co2_saved?: number;
          waste_reduced?: number;
          trees_planted?: number;
          water_saved?: number;
          energy_saved?: number;
        };
      };
      missions: {
        Row: {
          id: string;
          title: string;
          description: string;
          category: string;
          difficulty: 'easy' | 'medium' | 'hard';
          points: number;
          time_frame: 'daily' | 'weekly' | 'monthly';
          verification_method: 'photo' | 'location' | 'quiz' | 'self-report';
          icon: string;
          trending: boolean;
          active: boolean;
          created_at: string;
          updated_at: string;
        };
      };
      rewards: {
        Row: {
          id: string;
          title: string;
          description: string;
          cost: number;
          category: 'discount' | 'donation' | 'badge' | 'experience';
          image_url: string;
          icon: string;
          available: number;
          active: boolean;
          created_at: string;
          updated_at: string;
        };
      };
      impact_stats: {
        Row: {
          id: string;
          co2_saved: number;
          waste_reduced: number;
          trees_planted: number;
          water_saved: number;
          energy_saved: number;
          total_users: number;
          total_missions_completed: number;
          updated_at: string;
        };
      };
      completed_missions: {
        Row: {
          id: string;
          user_id: string;
          mission_id: string;
          completed_at: string;
          proof_url: string | null;
          verified: boolean;
          points_earned: number;
        };
        Insert: {
          user_id: string;
          mission_id: string;
          proof_url?: string;
          verified?: boolean;
          points_earned: number;
        };
      };
      social_posts: {
        Row: {
          id: string;
          user_id: string;
          content: string;
          image_url: string | null;
          mission_id: string | null;
          likes_count: number;
          created_at: string;
        };
        Insert: {
          user_id: string;
          content: string;
          image_url?: string;
          mission_id?: string;
        };
      };
    };
  };
}